import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Container, makeStyles, Typography } from '@material-ui/core';
import { Page } from 'src/components';
import { useCrmSalesTicket } from 'src/hooks/crm-sales/use-crm-sales';
import {
  CrmSalesFilterPayload,
  CrmSalesStatus
} from 'src/redux/slices/crm-sales/types';
import { crmSalesStatuses } from 'src/redux/slices/crm-sales';
import { convertToLocalISO, getDateRangePreset } from 'src/utils';
import Toolbar, { ToolbarState } from './component/Toolbar';
import {
  CrmColumns,
  CrmFormDialog,
  CrmFormValues,
  CrmSalesSummary
} from './component';
import { CrmTicket, CrmTicketCard } from './component/CrmTicketCard';
import { usePermissions } from 'src/hooks';
import { SupabaseClientService } from 'src/services';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  productCard: {
    height: '100%'
  }
}));

const CRM_SALES_PAGE_SIZE = 10;

const getCrmSalesFilters = (
  toolbarState?: ToolbarState
): CrmSalesFilterPayload => {
  const presetRange =
    toolbarState?.datePreset && toolbarState.datePreset !== 'custom'
      ? getDateRangePreset(toolbarState.datePreset)
      : null;

  return {
    search_customer: toolbarState?.search?.trim() || undefined,
    sort_by: toolbarState?.sortDirection
      ? `created_at:${toolbarState.sortDirection.toLowerCase()}`
      : undefined,
    start_date:
      toolbarState?.dateFrom ||
      (presetRange ? convertToLocalISO(presetRange.start) : undefined) ||
      '',
    end_date:
      toolbarState?.dateTo ||
      (presetRange ? convertToLocalISO(presetRange.end, true) : undefined) ||
      '',
    limit: CRM_SALES_PAGE_SIZE,
    offset: 0
  };
};

const getCrmSalesStatus = (status?: string): CrmSalesStatus | undefined =>
  crmSalesStatuses.find((item) => item === status);

const getTicketPayloadStatus = (ticket?: {
  status?: string;
}): CrmSalesStatus | undefined => getCrmSalesStatus(ticket?.status);

const getOptionalFieldValue = (value?: string) => value?.trim() || 'N/A';

const CrmSalesView = () => {
  const classes = useStyles();
  const {
    statuses,
    columns,
    fetchTicketsByStatus,
    fetchAllTickets,
    addTicket,
    updateTicket
  } = useCrmSalesTicket();
  const { canByPassCrmTicket } = usePermissions();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<CrmTicket | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeFilters, setActiveFilters] = useState<
    CrmSalesFilterPayload | undefined
  >({
    limit: CRM_SALES_PAGE_SIZE,
    offset: 0
  });
  const activeFiltersRef = useRef(activeFilters);
  const totalDealsCount = statuses.reduce(
    (count, status) =>
      status === 'Lost'
        ? count
        : count + (columns[status]?.tickets.length || 0),
    0
  );
  const projectedValue = statuses.reduce(
    (total, status) =>
      status === 'Lost'
        ? total
        : total +
          (columns[status]?.tickets || []).reduce(
            (sum, ticket) => sum + Number(ticket?.deal_value || 0),
            0
          ),
    0
  );

  const handleApplyFilters = useCallback((toolbarState?: ToolbarState) => {
    const filters = getCrmSalesFilters(toolbarState);

    setActiveFilters(filters);
  }, []);

  useEffect(() => {
    activeFiltersRef.current = activeFilters;
  }, [activeFilters]);

  useEffect(() => {
    fetchAllTickets(activeFilters);
  }, [activeFilters, fetchAllTickets]);

  useEffect(() => {
    const refetchAllStatuses = () => {
      fetchAllTickets({
        ...activeFiltersRef.current,
        limit: CRM_SALES_PAGE_SIZE,
        offset: 0
      });
    };

    const refetchStatusColumn = (status?: CrmSalesStatus) => {
      if (!status) return;

      fetchTicketsByStatus(status, {
        ...activeFiltersRef.current,
        limit: CRM_SALES_PAGE_SIZE,
        offset: 0
      });
    };

    const crmSalesTicketsTableChanges = SupabaseClientService.channel(
      'crm_sales_tickets_channel'
    )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_sales_tickets'
        },
        (payload) => {
          if (payload?.table !== 'crm_sales_tickets') return;

          const newStatus = getTicketPayloadStatus(payload?.new);
          const oldStatus = getTicketPayloadStatus(payload?.old);

          if (payload?.eventType === 'INSERT') {
            if (!newStatus) {
              refetchAllStatuses();
              return;
            }

            refetchStatusColumn(newStatus);
          }

          if (payload?.eventType === 'UPDATE') {
            if (!newStatus && !oldStatus) {
              refetchAllStatuses();
              return;
            }

            refetchStatusColumn(newStatus);

            if (oldStatus && oldStatus !== newStatus) {
              refetchStatusColumn(oldStatus);
            }
          }

          if (payload?.eventType === 'DELETE') {
            if (!oldStatus) {
              refetchAllStatuses();
              return;
            }

            refetchStatusColumn(oldStatus);
          }
        }
      )
      .subscribe((status, error) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.error('CRM sales realtime subscription failed', {
            status,
            error
          });
        }
      });

    return () => {
      SupabaseClientService.removeChannel(crmSalesTicketsTableChanges);
    };
  }, [fetchAllTickets, fetchTicketsByStatus]);

  const handleOpenAddDialog = () => {
    setSelectedTicket(null);
    setDialogOpen(true);
  };

  const handleOpenDetailsDialog = (ticket: CrmTicket) => {
    setSelectedTicket(ticket);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (isSubmitting) return;
    setDialogOpen(false);
    setSelectedTicket(null);
  };

  const handleSubmitTicket = async (values: CrmFormValues) => {
    try {
      setIsSubmitting(true);
      const dealValue =
        values.deal_value === '' ? undefined : Number(values.deal_value);
      const contactNo = getOptionalFieldValue(values.contact_no);
      const address = getOptionalFieldValue(values.address);

      if (typeof selectedTicket?.id === 'number') {
        await updateTicket(
          {
            id: selectedTicket.id,
            previous_status: getCrmSalesStatus(selectedTicket.status),
            data: {
              customer_name: values.customer_name,
              contact_no: contactNo,
              address,
              service_type: values.service_type,
              status: values.status,
              deal_value: dealValue,
              remarks: values.remarks,
              social_media_link: values.social_media_link
            }
          },
          activeFilters
        );
      } else {
        await addTicket(
          {
            customer_name: values.customer_name,
            contact_no: contactNo,
            address,
            service_type: values.service_type,
            status: values.status,
            deal_value: dealValue,
            remarks: values.remarks,
            social_media_link: values.social_media_link
          },
          activeFilters
        );
      }

      setDialogOpen(false);
      setSelectedTicket(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoadMoreTickets = useCallback(
    (status: string) => {
      const crmStatus = getCrmSalesStatus(status);

      if (!crmStatus) return;
      const column = columns[crmStatus];

      if (!column?.hasMore || column.loading) return;

      fetchTicketsByStatus(crmStatus, {
        ...activeFilters,
        limit: CRM_SALES_PAGE_SIZE,
        offset: column.tickets.length,
        append: true
      });
    },
    [activeFilters, columns, fetchTicketsByStatus]
  );

  return (
    <Page className={classes.root} title="CRM Sales">
      <Container maxWidth={false}>
        <Typography color="textPrimary" variant="h3">
          CRM Sales
        </Typography>
        <Toolbar
          onApply={handleApplyFilters}
          onAddTicket={handleOpenAddDialog}
        />
        <CrmSalesSummary
          dealsCount={totalDealsCount}
          projectedValue={projectedValue}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gridGap: 10,
            overflowX: 'auto',
            paddingBottom: 8,
            width: '100%'
          }}
        >
          {statuses.map((status) => (
            <CrmColumns
              key={status}
              status={status}
              tickets={columns[status]?.tickets || []}
              isLoading={columns[status]?.loading}
              hasMore={columns[status]?.hasMore}
              totalDealValue={(columns[status]?.tickets || []).reduce(
                (sum, ticket) => sum + Number(ticket?.deal_value || 0),
                0
              )}
              onLoadMore={handleLoadMoreTickets}
              renderTicket={(ticket) => (
                <CrmTicketCard
                  ticket={ticket}
                  onClick={handleOpenDetailsDialog}
                />
              )}
            />
          ))}
        </div>
        <CrmFormDialog
          open={dialogOpen}
          ticket={selectedTicket}
          loading={isSubmitting}
          isReadOnly={!canByPassCrmTicket}
          onClose={handleCloseDialog}
          onSubmit={handleSubmitTicket}
        />
      </Container>
    </Page>
  );
};

export default CrmSalesView;
